<template>
  <div class="schedule-stat-v3" v-loading="loading">
    <!-- Header Phân Hệ Thống Kê Thời Khóa Biểu -->
    <div class="stat-section-header">
      <div class="header-title-group">
        <div class="header-icon-badge">
          <i class="el-icon-time"></i>
        </div>
        <div>
          <h2 class="header-title">Thống Kê Thời Khóa Biểu & Tiến Độ Đăng Kế Hoạch</h2>
          <p class="header-subtitle">
            Theo dõi tiến độ đăng kế hoạch thời khóa biểu tuần của các lớp
          </p>
        </div>
      </div>

      <div class="header-badge-group">
        <span class="live-indicator">
          <span class="pulse-dot"></span>
        Đã đăng: {{ headerPublishedLabel }}
        </span>
      </div>
    </div>

    <!-- 1. Thanh Bộ Lọc Tuần & Khối Lớp -->
    <div class="filter-control-bar">
      <div class="filter-left">
        <!-- Bộ lọc Tuần: Tuần trước / Chọn tuần / Tuần sau -->
        <div class="filter-chip">
          <span class="chip-label"><i class="el-icon-date"></i> Chọn tuần:</span>
          <el-date-picker
            v-model="selectedWeekDate"
            type="week"
            size="small"
            format="Tuần WW (yyyy)"
            placeholder="Chọn tuần xem"
            :clearable="false"
            class="week-picker"
            @change="handleWeekChange"
          />
        </div>

        <div class="week-nav-btn-group">
          <button class="nav-arrow-btn" title="Tuần trước" @click="prevWeek">
            <i class="el-icon-arrow-left"></i>
          </button>
          <button class="nav-current-btn" @click="currentWeek">
            Tuần này
          </button>
          <button class="nav-arrow-btn" title="Tuần sau" @click="nextWeek">
            <i class="el-icon-arrow-right"></i>
          </button>
        </div>

        <div class="week-range-tag">
          <i class="el-icon-calendar"></i>
          <span>{{ weekRangeLabel }}</span>
        </div>

        <!-- Bộ lọc Khối -->
        <div class="filter-chip">
          <span class="chip-label"><i class="el-icon-collection-tag"></i> Khối:</span>
          <el-select
            v-model="selectedGrade"
            size="small"
            placeholder="Tất cả các khối"
            class="grade-select"
            @change="handleGradeChange"
          >
            <el-option
              v-for="g in gradeOptions"
              :key="g.value"
              :label="g.label"
              :value="g.value"
            />
          </el-select>
        </div>
      </div>

    </div>

    <el-alert
      v-if="errorMessage"
      class="schedule-error"
      title="Không tải được thống kê thời khóa biểu"
      :description="errorMessage"
      type="error"
      show-icon
      :closable="false"
    />

    <div v-if="hasData">
    <!-- 2. Cụm 3 Chỉ Số Chính (Bấm vào để mở Dialog danh sách) -->
    <div class="schedule-kpi-grid">
      <!-- 1. Tổng số lớp -->
      <div class="schedule-kpi-card border-indigo">
        <div class="kpi-icon-wrap bg-indigo-soft text-indigo">
          <i class="el-icon-s-home"></i>
        </div>
        <div class="kpi-details">
          <span class="kpi-name">Tổng số lớp học</span>
          <div class="kpi-number text-indigo">
          {{ hasData ? totalClassesCount : '—' }} <span class="kpi-unit">lớp</span>
          </div>
          <div class="kpi-hint">{{ selectedGradeLabel }}</div>
        </div>
      </div>

      <!-- 2. Số lớp đã đăng (CLICKABLE ➔ Mở Dialog Lớp Đã Đăng) -->
      <div
        class="schedule-kpi-card border-emerald interactive-card"
        @click="openScheduleDialog('published')"
      >
        <div class="kpi-icon-wrap bg-emerald-soft text-emerald">
          <i class="el-icon-circle-check"></i>
        </div>
        <div class="kpi-details">
          <div class="kpi-title-row">
            <span class="kpi-name">Số lớp đã đăng</span>
            <span class="click-hint-badge text-emerald">Xem chi tiết <i class="el-icon-arrow-right"></i></span>
          </div>
          <div class="kpi-number text-emerald">
            {{ hasData ? publishedCount : '—' }} <span class="kpi-unit">lớp</span>
          </div>
          <div class="kpi-hint text-emerald">
            <i class="el-icon-check"></i> Đạt {{ publishRate }}% {{ selectedGradeLabel }}
          </div>
        </div>
      </div>

      <!-- 3. Số lớp chưa đăng (CLICKABLE ➔ Mở Dialog Lớp Chưa Đăng) -->
      <div
        class="schedule-kpi-card border-rose interactive-card"
        @click="openScheduleDialog('unpublished')"
      >
        <div class="kpi-icon-wrap bg-rose-soft text-rose">
          <i class="el-icon-warning-outline"></i>
        </div>
        <div class="kpi-details">
          <div class="kpi-title-row">
            <span class="kpi-name">Số lớp chưa đăng</span>
            <span class="click-hint-badge text-rose">Xem chi tiết <i class="el-icon-arrow-right"></i></span>
          </div>
          <div class="kpi-number text-rose">
            {{ hasData ? unpublishedCount : '—' }} <span class="kpi-unit">lớp</span>
          </div>
          <div class="kpi-hint text-rose">
            <i class="el-icon-bell"></i> Còn {{ unpublishRate }}% cần nộp
          </div>
        </div>
      </div>
    </div>

    <!-- 3. Khu Vực Biểu Đồ & Trạng Thái Theo Khối -->
    <el-row :gutter="20" class="charts-row">
      <!-- Biểu đồ 1: Hai cột số lớp đã đăng và chưa đăng theo từng khối -->
      <el-col :xs="24" :sm="24" :md="14" :lg="15">
        <div class="chart-card h-100">
          <div class="chart-card-head">
            <div>
              <h3 class="chart-title">Tình Hình Đăng Thời Khóa Biểu Theo Khối</h3>
              <p class="chart-subtitle">{{ weekRangeLabel }} • So sánh số lớp đã đăng và chưa đăng</p>
            </div>
            <div class="chart-legend-chips">
              <span class="legend-dot-item"><span class="chip-color chip-pub"></span> Đã đăng</span>
              <span class="legend-dot-item"><span class="chip-color chip-unpub"></span> Chưa đăng</span>
            </div>
          </div>

          <div v-if="gradeStats.length" class="grade-chart-scroll">
            <div
              ref="chartGradeSchedule"
              class="grade-chart-canvas"
              :style="{ width: `${gradeStats.length * 118}px` }"
            ></div>
          </div>
          <p v-else class="grade-chart-empty">Không có lớp trong phạm vi đang chọn.</p>
        </div>
      </el-col>

      <!-- Biểu đồ 2: Tỷ lệ phủ sóng toàn trường (Gauge) -->
      <el-col :xs="24" :sm="24" :md="10" :lg="9">
        <div class="chart-card h-100">
          <div class="chart-card-head">
            <div>
              <h3 class="chart-title">Tỷ Lệ Lớp Đã Đăng Kế Hoạch Tuần</h3>
              <p class="chart-subtitle">Tiến độ hoàn thành đăng kế hoạch thời khóa biểu</p>
            </div>
            <span :class="['tag-pill', publishRate >= 90 ? 'tag-pill--emerald' : 'tag-pill--amber']">
              {{ publishRate >= 90 ? 'Hoàn thành tốt' : 'Cần đôn đốc' }}
            </span>
          </div>

          <div class="chart-container-gauge" ref="chartCoverageGauge"></div>

          <!-- Action Button nhanh mở Modal -->
          <div class="gauge-action-footer">
            <el-button
              type="success"
              size="small"
              icon="el-icon-circle-check"
              class="quick-open-btn"
              @click="openScheduleDialog('published')"
            >
              Danh sách đã đăng ({{ hasData ? publishedCount : '—' }})
            </el-button>
            <el-button
              type="danger"
              size="small"
              icon="el-icon-warning-outline"
              class="quick-open-btn"
              @click="openScheduleDialog('unpublished')"
            >
              Danh sách chưa đăng ({{ hasData ? unpublishedCount : '—' }})
            </el-button>
          </div>
        </div>
      </el-col>
    </el-row>
    </div>

    <!-- 4. DIALOG: DANH SÁCH LỚP ĐÃ ĐĂNG / CHƯA ĐĂNG THỜI KHÓA BIỂU -->
    <el-dialog
      :visible.sync="dialogVisible"
      width="60%"
      top="7vh"
      custom-class="modern-schedule-dialog"
      :destroy-on-close="true"
    >
      <div slot="title" class="dialog-custom-header">
        <div class="header-icon-box" :class="activeTab === 'published' ? 'box-emerald' : 'box-rose'">
          <i :class="activeTab === 'published' ? 'el-icon-circle-check' : 'el-icon-warning-outline'"></i>
        </div>
        <div>
          <h3 class="dialog-heading">
            {{ activeTab === 'published' ? 'Danh Sách Các Lớp Đã Đăng Thời Khóa Biểu' : 'Danh Sách Các Lớp Chưa Đăng Thời Khóa Biểu' }}
          </h3>
          <p class="dialog-subheading">
            <i class="el-icon-calendar"></i> {{ weekRangeLabel }} &nbsp;•&nbsp;
            <i class="el-icon-office-building"></i> Hệ thống Trường Mầm Non OneKids
          </p>
        </div>
      </div>

      <!-- Tab Switcher bên trong Dialog -->
      <div class="dialog-tab-bar">
        <button
          :class="['dialog-tab-btn', { active: activeTab === 'published' }]"
          @click="activeTab = 'published'"
        >
          <i class="el-icon-circle-check"></i>
          <span>Lớp đã đăng ({{ publishedList.length }})</span>
        </button>
        <button
          :class="['dialog-tab-btn', 'btn-danger-tab', { active: activeTab === 'unpublished' }]"
          @click="activeTab = 'unpublished'"
        >
          <i class="el-icon-warning-outline"></i>
          <span>Lớp chưa đăng ({{ unpublishedList.length }})</span>
        </button>

        <div class="dialog-search-wrap">
          <el-input
            v-model="searchQuery"
            size="small"
            placeholder="Tìm theo tên lớp hoặc khối..."
            prefix-icon="el-icon-search"
            clearable
            class="dialog-search-input"
          />
        </div>
      </div>

      <!-- Nội dung TAB 1: DANH SÁCH LỚP ĐÃ ĐĂNG -->
      <div v-if="activeTab === 'published'" class="dialog-table-container">
        <el-table
          :data="filteredPublishedList"
          stripe
          border
          size="small"
          class="modern-schedule-table"
          empty-text="Chưa có lớp nào đăng thời khóa biểu cho tuần này"
        >
          <el-table-column type="index" label="STT" width="55" align="center" />

          <el-table-column label="Tên Lớp" min-width="160">
            <template slot-scope="scope">
              <div class="class-title-cell">
                <strong class="text-indigo">{{ scope.row.className }}</strong>
                <span class="sub-grade">{{ scope.row.gradeName }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="Trạng thái duyệt" min-width="140" align="center">
            <template slot-scope="scope">
              {{ approvalLabel(scope.row.approvalStatus) }}
            </template>
          </el-table-column>

          <el-table-column label="Tệp kế hoạch" min-width="170">
            <template slot-scope="scope">
              <el-button
                v-for="file in scope.row.attachments"
                :key="file.attachmentId"
                type="text"
                size="mini"
                @click="downloadAttachment(file)"
              >
                <i class="el-icon-download"></i> {{ file.fileName || 'Tải tệp' }}
              </el-button>
            </template>
          </el-table-column>

          <el-table-column label="Cập nhật lần cuối" min-width="150" align="center">
            <template slot-scope="scope">{{ formatDateTime(scope.row.updatedAt) }}</template>
          </el-table-column>
        </el-table>
      </div>

      <!-- Nội dung TAB 2: DANH SÁCH LỚP CHƯA ĐĂNG -->
      <div v-else class="dialog-table-container">
        <el-table
          :data="filteredUnpublishedList"
          stripe
          border
          size="small"
          class="modern-schedule-table"
          empty-text="Tuyệt vời! Tất cả các lớp đã hoàn thành đăng thời khóa biểu"
        >
          <el-table-column type="index" label="STT" width="55" align="center" />

          <el-table-column label="Tên Lớp" min-width="160">
            <template slot-scope="scope">
              <div class="class-title-cell">
                <strong class="text-rose">{{ scope.row.className }}</strong>
                <span class="sub-grade">{{ scope.row.gradeName }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="Trạng thái" min-width="130" align="center">
            <template slot-scope="scope">{{ scope.row.published ? 'Đã đăng' : 'Chưa đăng' }}</template>
          </el-table-column>
        </el-table>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="dialogVisible = false">Đóng</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from "echarts";
import moment from "moment";
import WeeklyScheduleStatisticsService from "@/services/dashboard_v3/WeeklyScheduleStatisticsService";

export default {
  name: "ScheduleStatisticsV3",
  props: {
    autoLoad: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      hasLoadedData: false,
      isLoadingData: false,
      selectedWeekDate: moment().toDate(),
      selectedGrade: "",
      statistics: null,
      loading: false,
      errorMessage: "",
      requestSequence: 0,
      dialogVisible: false,
      activeTab: "published",
      searchQuery: "",
      chartGrade: null,
      chartGauge: null
    };
  },

  computed: {
    hasData() {
      return Boolean(this.statistics);
    },
    headerPublishedLabel() {
      return this.hasData ? `${this.publishedCount}/${this.totalClassesCount} lớp (${this.publishRate}%)` : "—";
    },
    weekRangeLabel() {
      const start = this.statistics && this.statistics.weekStartDate
        ? moment(this.statistics.weekStartDate, "YYYY-MM-DD")
        : moment(this.selectedWeekDate).startOf("isoWeek");
      const end = this.statistics && this.statistics.weekEndDate
        ? moment(this.statistics.weekEndDate, "YYYY-MM-DD")
        : start.clone().add(6, "days");
      return `Tuần ${start.isoWeek()} (${start.format("DD/MM/YYYY")} - ${end.format("DD/MM/YYYY")})`;
    },
    gradeOptions() {
      const options = [{ label: "Tất cả các khối", value: "" }];
      const grades = this.statistics && this.statistics.gradeOptions ? this.statistics.gradeOptions : [];
      return options.concat(grades.map(grade => ({
        label: grade.gradeName,
        value: grade.gradeId
      })));
    },
    selectedGradeLabel() {
      if (this.selectedGrade === "") return "Toàn bộ trường";
      const grade = this.gradeOptions.find(item => item.value === this.selectedGrade);
      return grade ? grade.label : "Khối đã chọn";
    },
    totalClassesCount() {
      return this.hasData ? this.statistics.totalClasses : 0;
    },
    publishedCount() {
      return this.hasData ? this.statistics.publishedClasses : 0;
    },
    unpublishedCount() {
      return this.hasData ? this.statistics.unpublishedClasses : 0;
    },
    publishRate() {
      return this.hasData ? Number(this.statistics.publishRate).toFixed(1) : "—";
    },
    unpublishRate() {
      return this.hasData ? Number(this.statistics.unpublishedRate).toFixed(1) : "—";
    },
    gradeStats() {
      return this.hasData ? this.statistics.gradeStats : [];
    },
    publishedList() {
      const classes = this.hasData ? this.statistics.classes : [];
      return classes.filter(item => item.published);
    },
    unpublishedList() {
      const classes = this.hasData ? this.statistics.classes : [];
      return classes.filter(item => !item.published);
    },
    filteredPublishedList() {
      return this.filterBySearch(this.publishedList);
    },
    filteredUnpublishedList() {
      return this.filterBySearch(this.unpublishedList);
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.initCharts();
      window.addEventListener("resize", this.handleResize);
      if (this.autoLoad) {
        this.loadData();
      }
    });
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
    this.disposeCharts();
  },

  methods: {
    async loadData() {
      if (this.hasLoadedData || this.isLoadingData) return;
      this.isLoadingData = true;
      try {
        await this.loadWeeklyStatistics();
        this.hasLoadedData = true;
      } finally {
        this.isLoadingData = false;
      }
    },
    filterBySearch(items) {
      const query = this.searchQuery.trim().toLocaleLowerCase("vi");
      if (!query) return items;
      return items.filter(item =>
        `${item.className || ""} ${item.gradeName || ""}`.toLocaleLowerCase("vi").includes(query)
      );
    },
    loadWeeklyStatistics() {
      const requestId = ++this.requestSequence;
      const weekStartDate = moment(this.selectedWeekDate).startOf("isoWeek").format("YYYY-MM-DD");
      const gradeId = this.selectedGrade === "" ? undefined : this.selectedGrade;
      this.loading = true;
      this.errorMessage = "";
      return WeeklyScheduleStatisticsService.getWeeklyProgress(weekStartDate, gradeId)
        .then(response => {
          if (requestId !== this.requestSequence) return;
          const data = response && response.data && response.data.data;
          if (!data || !Array.isArray(data.classes) || !Array.isArray(data.gradeStats)) {
            throw new Error("Phản hồi thống kê không đúng định dạng.");
          }
          this.statistics = data;
          this.$nextTick(() => this.updateCharts());
        })
        .catch(error => {
          if (requestId !== this.requestSequence) return;
          this.statistics = null;
          this.errorMessage = error && error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Vui lòng thử lại sau.";
          this.$nextTick(() => {
            this.disposeCharts();
            this.updateCharts();
          });
        })
        .finally(() => {
          if (requestId === this.requestSequence) this.loading = false;
        });
    },
    handleWeekChange() {
      this.loadWeeklyStatistics();
    },
    prevWeek() {
      this.selectedWeekDate = moment(this.selectedWeekDate).subtract(1, "weeks").toDate();
      this.loadWeeklyStatistics();
    },
    nextWeek() {
      this.selectedWeekDate = moment(this.selectedWeekDate).add(1, "weeks").toDate();
      this.loadWeeklyStatistics();
    },
    currentWeek() {
      this.selectedWeekDate = moment().toDate();
      this.loadWeeklyStatistics();
    },
    handleGradeChange() {
      this.loadWeeklyStatistics();
    },
    openScheduleDialog(tabName) {
      this.activeTab = tabName;
      this.searchQuery = "";
      this.dialogVisible = true;
    },
    approvalLabel(status) {
      if (status === "APPROVED") return "Đã duyệt";
      if (status === "PENDING_APPROVAL") return "Chờ duyệt";
      return "—";
    },
    formatDateTime(value) {
      return value ? moment(value).format("DD/MM/YYYY HH:mm") : "—";
    },
    downloadAttachment(file) {
      WeeklyScheduleStatisticsService.downloadAttachment(file.attachmentId)
        .then(response => {
          const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = file.fileName || "ke-hoach-tuan";
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
        })
        .catch(() => this.$message.error("Không tải được tệp kế hoạch."));
    },
    handleResize() {
      if (this.chartGrade) this.chartGrade.resize();
      if (this.chartGauge) this.chartGauge.resize();
    },
    disposeCharts() {
      if (this.chartGrade) {
        this.chartGrade.dispose();
        this.chartGrade = null;
      }
      if (this.chartGauge) {
        this.chartGauge.dispose();
        this.chartGauge = null;
      }
    },
    initCharts() {
      this.renderGradeChart();
      this.renderGaugeChart();
    },
    updateCharts() {
      this.renderGradeChart();
      this.renderGaugeChart();
    },
    renderGradeChart() {
      const dom = this.$refs.chartGradeSchedule;
      if (!dom) {
        if (this.chartGrade) {
          this.chartGrade.dispose();
          this.chartGrade = null;
        }
        return;
      }
      if (!this.chartGrade) this.chartGrade = echarts.init(dom);
      const grades = this.gradeStats;
      this.chartGrade.setOption({
        tooltip: {
          trigger: "axis",
          axisPointer: { type: "shadow" },
          renderMode: "richText",
          backgroundColor: "#1e293b",
          borderWidth: 0,
          textStyle: { color: "#ffffff", fontSize: 12 },
          formatter: params => {
            const grade = grades[params[0].dataIndex];
            if (!grade) return "";
            return `${grade.gradeName}\nTổng: ${grade.totalClasses} lớp\n`
              + `Đã đăng: ${grade.publishedClasses} lớp\n`
              + `Chưa đăng: ${grade.unpublishedClasses} lớp\n`
              + `Tỷ lệ đã đăng: ${Number(grade.publishRate).toFixed(1)}%`;
          }
        },
        grid: { top: 35, left: 42, right: 14, bottom: 46 },
        xAxis: {
          type: "category",
          data: grades.map(grade => grade.gradeName),
          axisLine: { lineStyle: { color: "#cbd5e1" } },
          axisTick: { show: false },
          axisLabel: {
            interval: 0,
            color: "#475569",
            fontWeight: 600,
            fontSize: 11,
            formatter: value => value.length > 15 ? `${value.slice(0, 8)}…${value.slice(-6)}` : value
          }
        },
        yAxis: {
          type: "value",
          name: "Lớp",
          min: 0,
          minInterval: 1,
          nameTextStyle: { color: "#94a3b8", fontSize: 11 },
          axisLabel: { color: "#94a3b8" },
          splitLine: { lineStyle: { color: "#e8eef7" } }
        },
        series: [
          {
            name: "Đã đăng",
            type: "bar",
            barWidth: 22,
            barGap: "15%",
            data: grades.map(grade => grade.publishedClasses),
            itemStyle: { color: "#10b981", borderRadius: [5, 5, 0, 0] }
          },
          {
            name: "Chưa đăng",
            type: "bar",
            barWidth: 22,
            data: grades.map(grade => grade.unpublishedClasses),
            itemStyle: { color: "#f43f5e", borderRadius: [5, 5, 0, 0] }
          }
        ]
      }, true);
      this.chartGrade.resize();
    },
    renderGaugeChart() {
      const dom = this.$refs.chartCoverageGauge;
      if (!dom) return;
      if (!this.chartGauge) this.chartGauge = echarts.init(dom);
      const rate = this.hasData ? this.statistics.publishRate : 0;
      this.chartGauge.setOption({
        series: [{
          type: "gauge", startAngle: 210, endAngle: -30, min: 0, max: 100, splitNumber: 10,
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: "#f59e0b" }, { offset: 0.5, color: "#3b82f6" }, { offset: 1, color: "#10b981" }
            ]), shadowColor: "rgba(16, 185, 129, 0.35)", shadowBlur: 10
          },
          progress: { show: true, roundCap: true, width: 14 },
          pointer: { show: false },
          axisLine: { roundCap: true, lineStyle: { width: 14, color: [[1, "#f1f5f9"]] } },
          axisTick: { show: false }, splitLine: { show: false }, axisLabel: { show: false },
          title: { show: true, offsetCenter: [0, "20%"], fontSize: 13, color: "#64748b", fontWeight: 600 },
          detail: { valueAnimation: true, offsetCenter: [0, "-15%"], fontSize: 34, fontWeight: 800, formatter: "{value}%", color: "#0f172a" },
          data: [{ value: rate, name: "Lớp đã đăng kế hoạch tuần" }]
        }]
      }, true);
    }
  }
};
</script>

<style scoped>
.schedule-stat-v3 {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 4px 20px -3px rgba(0, 0, 0, 0.04);
  margin-bottom: 28px;
}

/* Header */
.stat-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 18px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
}

.header-title-group {
  display: flex;
  align-items: center;
  gap: 14px;
}

.header-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: #ede9fe;
  color: #7c3aed;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.header-title {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.header-subtitle {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.live-indicator {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #059669;
  background: #ecfdf5;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid #a7f3d0;
}

.pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.25);
  animation: pulseDot 2s infinite;
}

@keyframes pulseDot {
  0% { transform: scale(0.95); opacity: 0.8; }
  50% { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(0.95); opacity: 0.8; }
}

/* 1. Control Toolbar */
.filter-control-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 10px 16px;
  margin-bottom: 22px;
}

.filter-left {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.filter-chip {
  display: flex;
  align-items: center;
  gap: 6px;
}

.chip-label {
  font-size: 12.5px;
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
}

.week-picker {
  width: 175px;
}

.grade-select {
  width: 185px;
}

.week-nav-btn-group {
  display: inline-flex;
  background: #e2e8f0;
  border-radius: 8px;
  padding: 2px;
}

.nav-arrow-btn {
  border: none;
  background: transparent;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #475569;
  transition: all 0.15s ease;
}

.nav-arrow-btn:hover {
  background: #ffffff;
  color: #2563eb;
}

.nav-current-btn {
  border: none;
  background: transparent;
  padding: 0 10px;
  height: 28px;
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.15s ease;
}

.nav-current-btn:hover {
  background: #ffffff;
  color: #2563eb;
}

.week-range-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #4338ca;
  background: #e0e7ff;
  padding: 5px 12px;
  border-radius: 8px;
}

/* 2. Cụm 3 KPI Cards: Tổng số lớp, Đã đăng, Chưa đăng */
.schedule-kpi-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.schedule-kpi-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 16px 18px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  transition: all 0.2s ease;
}

.interactive-card {
  cursor: pointer;
  position: relative;
}

.interactive-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
  background: #ffffff;
}

.kpi-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.click-hint-badge {
  font-size: 10.5px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.kpi-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}

.kpi-details {
  flex: 1;
  min-width: 0;
}

.kpi-name {
  display: block;
  font-size: 12.5px;
  font-weight: 600;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kpi-number {
  font-size: 24px;
  font-weight: 800;
  line-height: 1.15;
  margin-bottom: 4px;
}

.kpi-unit {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
}

.kpi-hint {
  font-size: 11.5px;
  color: #94a3b8;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.border-indigo { border-left: 4px solid #4f46e5; }
.border-emerald { border-left: 4px solid #059669; }
.border-rose { border-left: 4px solid #ef4444; }
.border-amber { border-left: 4px solid #f59e0b; }

.text-indigo { color: #4f46e5; }
.text-emerald { color: #059669; }
.text-rose { color: #ef4444; }
.text-amber { color: #f59e0b; }

.bg-indigo-soft { background: #e0e7ff; }
.bg-emerald-soft { background: #ecfdf5; }
.bg-rose-soft { background: #ffe4e6; }
.bg-amber-soft { background: #fef3c7; }

/* 3. Charts */
.charts-row {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.charts-row::before,
.charts-row::after {
  display: none;
}

.charts-row > .el-col {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.chart-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  box-sizing: border-box;
}

.h-100 {
  height: 100%;
}

.chart-card-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 12px;
}

.chart-title {
  margin: 0 0 3px;
  font-size: 15.5px;
  font-weight: 700;
  color: #1e293b;
}

.chart-subtitle {
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
}

.chart-legend-chips {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
}

.legend-dot-item {
  display: flex;
  align-items: center;
  gap: 5px;
}

.chip-color {
  width: 10px;
  height: 10px;
  border-radius: 2px;
}

.chip-pub { background: #10b981; }
.chip-unpub { background: #f43f5e; }

.tag-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 8px;
  white-space: nowrap;
}

.tag-pill--emerald { background: #dcfce7; color: #15803d; }
.tag-pill--amber { background: #fef3c7; color: #b45309; }

.grade-chart-scroll {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
}

.grade-chart-canvas {
  min-width: 100%;
  height: 270px;
}

.grade-chart-empty {
  margin: 40px 0;
  color: #94a3b8;
  text-align: center;
  font-size: 13px;
}

.chart-container-gauge {
  width: 100%;
  height: 200px;
  position: relative;
}

.gauge-action-footer {
  display: flex;
  gap: 10px;
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}

.quick-open-btn {
  flex: 1;
  border-radius: 8px;
  font-weight: 600;
}

/* 4. MODAL DIALOG STYLES */
.dialog-custom-header {
  display: flex;
  align-items: center;
  gap: 14px;
}

.header-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.box-emerald {
  background: #ecfdf5;
  color: #059669;
}

.box-rose {
  background: #ffe4e6;
  color: #e11d48;
}

.dialog-heading {
  margin: 0 0 3px;
  font-size: 17px;
  font-weight: 800;
  color: #0f172a;
}

.dialog-subheading {
  margin: 0;
  font-size: 12.5px;
  color: #64748b;
}

.dialog-tab-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;
  flex-wrap: wrap;
}

.dialog-tab-btn {
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  padding: 8px 16px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  transition: all 0.2s ease;
}

.dialog-tab-btn.active {
  background: #ecfdf5;
  color: #059669;
  border-color: #a7f3d0;
  box-shadow: 0 2px 6px rgba(5, 150, 105, 0.12);
}

.dialog-tab-btn.btn-danger-tab.active {
  background: #ffe4e6;
  color: #e11d48;
  border-color: #fecdd3;
  box-shadow: 0 2px 6px rgba(225, 29, 72, 0.12);
}

.dialog-search-wrap {
  margin-left: auto;
}

.dialog-search-input {
  width: 250px;
}

.dialog-table-container {
  max-height: 420px;
  overflow-y: auto;
}

.modern-schedule-table {
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
}

.class-title-cell {
  display: flex;
  flex-direction: column;
}

.sub-grade {
  font-size: 11px;
  color: #94a3b8;
}

.time-stamp {
  font-size: 12px;
  color: #475569;
}

.file-type-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.type-standard { background: #ede9fe; color: #7c3aed; }
.type-excel { background: #e0f2fe; color: #0284c7; }

.status-chip {
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.status-chip--success { background: #dcfce7; color: #15803d; }
.status-chip--warning { background: #fef3c7; color: #b45309; }
.status-chip--danger { background: #fee2e2; color: #b91c1c; }

.reminder-action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  background: #fff1f2;
  border: 1px solid #fecdd3;
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.reminder-text {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: #9f1239;
}

/* Responsive */
@media (max-width: 992px) {
  .schedule-kpi-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .filter-control-bar {
    flex-direction: column;
    align-items: flex-start;
  }
  .schedule-kpi-grid {
    grid-template-columns: 1fr;
  }
  .chart-card-head {
    flex-wrap: wrap;
  }
}
</style>
